import React, { useEffect, useMemo, useState } from "react";
import { Button, Divider, Form, Input, InputNumber, Modal, Select, Space, Tree, Typography, message } from "antd";
import {
    CreateEventLeaf,
    DeleteConditionItem,
    PatchBasicCondition,
    PatchCondition,
} from "../../api/v2/conditions";
import {appFetch} from "../../utils/appFetch";
import {CreateChildGroup, GetConditionTreeBundle} from "../../api/v2/conditionTree";

export interface AppFetchResponse {
    data: any;
    status: number;
}

function unwrap<T>(res: AppFetchResponse): { ok: boolean; data?: T; error?: string } {
    const ok = res.status >= 200 && res.status < 300;
    const payload = res?.data;
    if (payload && typeof payload === "object" && "data" in payload) {
        return { ok, data: (payload as any).data as T, error: (payload as any).error };
    }
    return { ok, data: payload as T, error: (payload as any)?.error };
}

type TreeBundle = {
    root_condition_id: number;
    conditionsById: Record<string, any>;
    itemsByConditionId: Record<string, any[]>;
    basicById: Record<string, any>;
};

const existsOps = [
    { value: "EXISTS", label: "EXISTS" },
    { value: "NOT_EXISTS", label: "NOT_EXISTS" },
] as const;

const scopeOps = [
    { value: "any", label: "Any entity" },
    { value: "run_context", label: "Same entity as trigger" },
] as const;


const countOps = [
    { value: ">=", label: ">=" },
    { value: ">", label: ">" },
    { value: "<=", label: "<=" },
    { value: "<", label: "<" },
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
] as const;

type Props = {
    domain: string;
    rootConditionId: number;
    open: boolean;
    onClose: () => void;
    onChanged?: () => void; // refresh triggers + trackers
    eventSuggestions?: string[];
};

export default function ConditionTreeEditorModal(props: Props) {
    const { domain, rootConditionId, open, onClose, onChanged, eventSuggestions = [] } = props;
    const { Title, Text } = Typography;

    const [bundle, setBundle] = useState<TreeBundle | null>(null);
    const [loading, setLoading] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState<number>(rootConditionId);

    const [addEventValue, setAddEventValue] = useState<string>("");

    const loadBundle = async () => {
        setLoading(true);
        try {
            const u = await GetConditionTreeBundle(domain, rootConditionId);

            if (!u.ok || !u.data) {
                message.error(u.error || "Failed loading condition tree");
                setBundle(null);
                return;
            }

            setBundle(u.data);

            // keep selection valid (FIX: no param typing here)
            const root = u.data.root_condition_id;
            setSelectedGroupId((prev) => (u.data && u.data.conditionsById[String(prev)] ? prev : root));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        setSelectedGroupId(rootConditionId);
        setAddEventValue("");
        loadBundle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, rootConditionId]);

    const selectedGroup = bundle?.conditionsById?.[String(selectedGroupId)] || null;
    const selectedItems = (bundle?.itemsByConditionId?.[String(selectedGroupId)] || []) as any[];
    const basicById = bundle?.basicById || {};

    const childGroups = selectedItems.filter((x) => x.item_type === "condition");
    const leafItems = selectedItems.filter((x) => x.item_type === "basic_condition");

    // Build tree nodes for left panel (groups only; leaves are edited on the right)
    const treeData = useMemo(() => {
        if (!bundle) return [];

        const buildNode = (condId: number): any => {
            const c = bundle.conditionsById[String(condId)];
            const childrenItems = (bundle.itemsByConditionId[String(condId)] || []).filter((i) => i.item_type === "condition");
            return {
                key: `cond:${condId}`,
                title: `${c?.title || "Group"} (${c?.operator || "AND"})`,
                children: childrenItems.map((ci) => buildNode(Number(ci.item_id))),
            };
        };

        return [buildNode(bundle.root_condition_id)];
    }, [bundle]);

    const updateGroupSettings = async (patch: any) => {
        if (!selectedGroupId) return;
        const res = await PatchCondition(domain, selectedGroupId, patch);
        if (!res.ok) {
            message.error(res.error || "Failed updating group");
            return;
        }
        await loadBundle();
        onChanged?.();
    };

    // NOTE: createChildGroupAndAttach endpoint assumed: POST /v2/conditions/:conditionId/group
    const addGroup = async () => {
        const parentId = selectedGroupId;

        const u = await CreateChildGroup(domain, parentId, {
            operator: "AND",
            title: "Group",
            sort_order: selectedItems.length,
        });

        if (!u.ok) {
            message.error(u.error || "Failed adding group");
            return;
        }

        await loadBundle();
        onChanged?.();
    };

    const addLeaf = async () => {
        const event_name = (addEventValue || "").trim();

        if (!event_name) {
            message.error("Event name is required");
            return;
        }

        const res = await CreateEventLeaf(domain, selectedGroupId, {
            event_name,
            aggregation: "exists",
            operator: "EXISTS",
            threshold: null,
            sort_order: selectedItems.length,
        });

        if (!res.ok) {
            message.error(res.error || "Failed adding event leaf");
            return;
        }

        setAddEventValue("");
        await loadBundle();
        onChanged?.();
    };

    const renderLeafRow = (ci: any) => {
        const basicId = Number(ci.item_id);
        const basic = basicById[String(basicId)];
        if (!basic) return null;

        const agg: "exists" | "count" = basic.aggregation === "count" ? "count" : "exists";
        const op = basic.operator || (agg === "count" ? ">=" : "EXISTS");
        const thresholdVal = basic.threshold ?? null;

        // ---- scope support via filters_json.entity.match = "run_context"
        let parsedFilters: any = null;
        try {
            parsedFilters =
                basic.filters_json && typeof basic.filters_json === "string"
                    ? JSON.parse(basic.filters_json)
                    : basic.filters_json || null;
        } catch {
            parsedFilters = null;
        }

        const scopeValue = parsedFilters?.entity?.match === "run_context" ? "run_context" : "any";

        return (
            <div key={`leaf-${ci.id}`} className="v2cte-leafRow">
                <Space wrap>
                    <Input
                        size="small"
                        className="v2cte-leafEvent"
                        defaultValue={basic.event_name || ""}
                        placeholder='e.g. "product.view"'
                        onBlur={async (e) => {
                            const next = e.target.value.trim();
                            if (!next || next === (basic.event_name || "")) return;
                            const r = await PatchBasicCondition(domain, basicId, { event_name: next });
                            if (!r.ok) message.error(r.error || "Failed saving");
                            else {
                                await loadBundle();
                                onChanged?.();
                            }
                        }}
                    />

                    <Select
                        size="small"
                        className="v2cte-leafAgg"
                        value={agg}
                        options={[
                            { value: "exists", label: "exists" },
                            { value: "count", label: "count" },
                        ]}
                        onChange={async (nextAgg) => {
                            if (nextAgg === agg) return;
                            const patch: any = { aggregation: nextAgg };
                            if (nextAgg === "exists") {
                                patch.operator = "EXISTS";
                                patch.threshold = null;
                            } else {
                                patch.operator = ">=";
                                patch.threshold = 1;
                            }
                            const r = await PatchBasicCondition(domain, basicId, patch);
                            if (!r.ok) message.error(r.error || "Failed saving");
                            else {
                                await loadBundle();
                                onChanged?.();
                            }
                        }}
                    />

                    <Select
                        size="small"
                        className="v2cte-leafOp"
                        value={op}
                        options={(agg === "count" ? countOps : existsOps) as any}
                        onChange={async (nextOp) => {
                            const patch: any = { operator: nextOp };
                            if (agg === "exists") patch.threshold = null;
                            else {
                                const num = Number(thresholdVal);
                                patch.threshold = Number.isFinite(num) ? num : 1;
                            }
                            const r = await PatchBasicCondition(domain, basicId, patch);
                            if (!r.ok) message.error(r.error || "Failed saving");
                            else {
                                await loadBundle();
                                onChanged?.();
                            }
                        }}
                    />

                    {agg === "count" ? (
                        <InputNumber
                            size="small"
                            className="v2cte-leafThreshold"
                            min={0}
                            value={Number(thresholdVal ?? 1)}
                            onChange={async (v) => {
                                const num = typeof v === "number" ? v : Number(v);
                                const next = Number.isFinite(num) ? num : 1;
                                const r = await PatchBasicCondition(domain, basicId, {
                                    aggregation: "count",
                                    operator: op,
                                    threshold: next,
                                });
                                if (!r.ok) message.error(r.error || "Failed saving");
                                else {
                                    await loadBundle();
                                    onChanged?.();
                                }
                            }}
                        />
                    ) : null}

                    <Select
                        size="small"
                        className="v2cte-leafScope"
                        value={scopeValue}
                        options={scopeOps as any}
                        onChange={async (v) => {
                            const nextFilters =
                                parsedFilters && typeof parsedFilters === "object" ? { ...parsedFilters } : {};

                            if (v === "run_context") {
                                nextFilters.entity = { match: "run_context" };
                            } else {
                                if (nextFilters.entity) delete nextFilters.entity;
                            }

                            const r = await PatchBasicCondition(domain, basicId, { filters_json: nextFilters });
                            if (!r.ok) message.error(r.error || "Failed saving");
                            else {
                                await loadBundle();
                                onChanged?.();
                            }
                        }}
                    />


                    <Button
                        size="small"
                        danger
                        onClick={async () => {
                            const r = await DeleteConditionItem(domain, Number(ci.id));
                            if (!r.ok) message.error(r.error || "Failed deleting");
                            else {
                                await loadBundle();
                                onChanged?.();
                            }
                        }}
                    >
                        Remove
                    </Button>
                </Space>
            </div>
        );
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1100}
            zIndex={13000}
            title="Condition tree"
        >
            <div className="v2cte-layout">
                {/* Left: groups tree */}
                <div className="v2cte-left">
                    <Title level={5} className="v2cte-title">Groups</Title>
                    <Tree
                        treeData={treeData as any}
                        defaultExpandAll
                        selectedKeys={[`cond:${selectedGroupId}`]}
                        onSelect={(keys) => {
                            const k = String(keys?.[0] || "");
                            if (!k.startsWith("cond:")) return;
                            const id = Number(k.split(":")[1]);
                            if (id) setSelectedGroupId(id);
                        }}
                    />
                </div>

                {/* Right: selected group editor */}
                <div className="v2cte-right">
                    <Title level={5} className="v2cte-title">Group settings</Title>

                    {!selectedGroup ? (
                        <Text type="secondary">Select a group</Text>
                    ) : (
                        <>
                            <div className="v2cte-groupSettings">
                                <Space wrap>
                                    <Input
                                        className="v2cte-groupTitle"
                                        size="small"
                                        defaultValue={selectedGroup.title || ""}
                                        placeholder="Group title"
                                        onBlur={(e) => {
                                            const next = e.target.value;
                                            if (next === (selectedGroup.title || "")) return;
                                            updateGroupSettings({ title: next || null });
                                        }}
                                    />

                                    <Select
                                        className="v2cte-groupOp"
                                        size="small"
                                        value={selectedGroup.operator}
                                        options={[
                                            { value: "AND", label: "AND" },
                                            { value: "OR", label: "OR" },
                                        ]}
                                        onChange={(v) => updateGroupSettings({ operator: v })}
                                    />

                                    <InputNumber
                                        className="v2cte-groupTimeframe"
                                        size="small"
                                        min={0}
                                        value={selectedGroup.timeframe_seconds ?? null}
                                        placeholder="timeframe (sec)"
                                        onChange={(v) => {
                                            const num = v === null || v === undefined ? null : Number(v);
                                            updateGroupSettings({ timeframe_seconds: Number.isFinite(num as any) ? num : null });
                                        }}
                                    />
                                </Space>
                            </div>

                            <Divider />

                            <Title level={5} className="v2cte-title">Add to this group</Title>

                            <div className="v2cte-addRow">
                                <Space wrap>
                                    {/* Event name - real input (no more Select weirdness) */}
                                    <Input
                                        className="v2cte-addEventInput"
                                        value={addEventValue}
                                        placeholder='Event name (e.g. "page.view")'
                                        onChange={(e) => setAddEventValue(e.target.value)}
                                        onPressEnter={() => addLeaf()}
                                    />

                                    {/* Optional suggestions (kept as a dropdown button) */}
                                    {eventSuggestions?.length ? (
                                        <Select
                                            className="v2cte-addEventSuggestions"
                                            placeholder="Suggestions"
                                            value={undefined}
                                            options={eventSuggestions.map((x) => ({ value: x, label: x }))}
                                            onChange={(v) => setAddEventValue(String(v || ""))}
                                            showSearch
                                            filterOption={(input, option) =>
                                                String(option?.value || "").toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    ) : null}

                                    <Button type="primary" onClick={addLeaf} loading={loading}>
                                        Add event leaf
                                    </Button>

                                    <Button onClick={addGroup} loading={loading}>
                                        Add group
                                    </Button>
                                </Space>
                            </div>

                            <Divider />

                            <Title level={5} className="v2cte-title">Leaves</Title>
                            {leafItems.length ? leafItems.map(renderLeafRow) : <Text type="secondary">No leaves in this group</Text>}

                            <Divider />

                            <Title level={5} className="v2cte-title">Child groups</Title>
                            {childGroups.length ? (
                                <div className="v2cte-childGroups">
                                    {childGroups.map((ci: any) => {
                                        const childId = Number(ci.item_id);
                                        const child = bundle?.conditionsById?.[String(childId)];
                                        return (
                                            <div key={`child-${ci.id}`} className="v2cte-childGroupRow">
                                                <Space wrap>
                                                    <Text>{child?.title || "Group"} ({child?.operator || "AND"})</Text>
                                                    <Button size="small" onClick={() => setSelectedGroupId(childId)}>Select</Button>
                                                    <Button
                                                        danger
                                                        size="small"
                                                        onClick={async () => {
                                                            // unlink (safe). delete condition_item only
                                                            const r = await DeleteConditionItem(domain, Number(ci.id));
                                                            if (!r.ok) message.error(r.error || "Failed removing group link");
                                                            else {
                                                                await loadBundle();
                                                                onChanged?.();
                                                            }
                                                        }}
                                                    >
                                                        Remove from group
                                                    </Button>
                                                </Space>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <Text type="secondary">No child groups</Text>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
}
